export interface ViewportSize {
  width: number;
  height: number;
}

export interface ProjectedWorldObject {
  lane: number;
  distance: number;
}

export interface ViewMotion {
  leanX: number;
  bobY: number;
}

export interface ProjectionFrame {
  width: number;
  height: number;
  screenCenterX: number;
  horizonY: number;
  playerGroundY: number;
  farRoadHalfWidth: number;
  nearRoadHalfWidth: number;
  maxDistance: number;
  perspectivePower: number;
  farScale: number;
  nearScale: number;
  laneSpread: number;
  recycleDistance: number;
  playerDistance: number;
}

export interface ProjectedPose {
  x: number;
  y: number;
  scale: number;
  depth: number;
  roadHalfWidth: number;
  closeness: number;
  perspective: number;
}

export const NO_MOTION: ViewMotion = { leanX: 0, bobY: 0 };

const HORIZON_RATIO = 0.33;
const PLAYER_GROUND_RATIO = 0.8;
const FAR_ROAD_RATIO = 0.016;
const NEAR_ROAD_RATIO = 0.4;
const PERSPECTIVE_POWER = 1.45;
const FAR_SCALE = 0.09;
const NEAR_SCALE = 0.6;
const LANE_SPREAD = 0.56;
const MAX_DISTANCE = 1;
const RECYCLE_DISTANCE = -0.22;
const PLAYER_DISTANCE = 0.03;
const SCALE_T_MAX = 1.18;

export function createProjectionFrame(viewport: ViewportSize): ProjectionFrame {
  const width = Math.max(1, viewport.width);
  const height = Math.max(1, viewport.height);
  return {
    width,
    height,
    screenCenterX: width * 0.5,
    horizonY: height * HORIZON_RATIO,
    playerGroundY: height * PLAYER_GROUND_RATIO,
    farRoadHalfWidth: width * FAR_ROAD_RATIO,
    nearRoadHalfWidth: width * NEAR_ROAD_RATIO,
    maxDistance: MAX_DISTANCE,
    perspectivePower: PERSPECTIVE_POWER,
    farScale: FAR_SCALE,
    nearScale: NEAR_SCALE,
    laneSpread: LANE_SPREAD,
    recycleDistance: RECYCLE_DISTANCE,
    playerDistance: PLAYER_DISTANCE,
  };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function distanceFromTiming(
  reachAtMs: number,
  elapsedMs: number,
  leadMs: number,
  maxDistance = MAX_DISTANCE,
): number {
  return ((reachAtMs - elapsedMs) / Math.max(1, leadMs)) * maxDistance;
}

export function shouldRecycle(distance: number, frame: ProjectionFrame): boolean {
  return distance < frame.recycleDistance;
}

export function approachAlpha(distance: number, frame: ProjectionFrame): number {
  if (distance > frame.maxDistance) {
    return 0;
  }
  const fadeInFrom = frame.maxDistance * 0.9;
  if (distance > fadeInFrom) {
    return clamp((frame.maxDistance - distance) / (frame.maxDistance - fadeInFrom), 0, 1);
  }
  if (distance < 0) {
    return clamp(1 + distance / Math.abs(frame.recycleDistance), 0, 1);
  }
  return 1;
}

export function sceneryLane(side: -1 | 1, spread = 2.2): number {
  return side * spread;
}

export function projectWorld(
  object: ProjectedWorldObject,
  frame: ProjectionFrame,
  motion: ViewMotion = NO_MOTION,
): ProjectedPose {
  return projectLane(object.distance, object.lane, frame, motion);
}

export function distanceForProjectedY(targetY: number, frame: ProjectionFrame): number {
  const span = frame.playerGroundY - frame.horizonY;
  if (span <= 1e-6) {
    return frame.playerDistance;
  }
  const perspective = (targetY - frame.horizonY) / span;
  if (perspective <= 0) {
    return frame.maxDistance;
  }
  const closeness = Math.pow(perspective, 1 / frame.perspectivePower);
  return frame.maxDistance * (1 - closeness);
}

export function projectLane(
  distance: number,
  lane: number,
  frame: ProjectionFrame,
  motion: ViewMotion = NO_MOTION,
): ProjectedPose {
  const closeness = 1 - distance / frame.maxDistance;
  const perspective =
    closeness >= 0 ? Math.pow(closeness, frame.perspectivePower) : 0;
  const screenY = frame.horizonY + perspective * (frame.playerGroundY - frame.horizonY) + motion.bobY * (0.25 + 0.75 * clamp(perspective, 0, 1));
  const roadHalfWidth = lerp(frame.farRoadHalfWidth, frame.nearRoadHalfWidth, clamp(perspective, 0, 1.12));
  const screenX =
    frame.screenCenterX +
    lane * roadHalfWidth * frame.laneSpread +
    motion.leanX * clamp(perspective, 0, 1.1);
  const scale = lerp(frame.farScale, frame.nearScale, clamp(perspective, 0, SCALE_T_MAX));
  const depth = Math.round(clamp(perspective, 0, 1.45) * 1000);

  return {
    x: finiteOr(screenX, frame.screenCenterX),
    y: finiteOr(screenY, frame.playerGroundY),
    scale: finiteOr(Math.max(0.01, scale), frame.farScale),
    depth: finiteOr(depth, 0),
    roadHalfWidth: finiteOr(Math.max(0, roadHalfWidth), frame.farRoadHalfWidth),
    closeness: finiteOr(closeness, 0),
    perspective: finiteOr(perspective, 0),
  };
}

export function laneGap(distance: number, frame: ProjectionFrame, motion: ViewMotion = NO_MOTION): number {
  const center = projectLane(distance, 0, frame, motion);
  const right = projectLane(distance, 1, frame, motion);
  return right.x - center.x;
}

export function isOnScreen(pose: ProjectedPose, frame: ProjectionFrame, margin = 160): boolean {
  return pose.x > -margin && pose.x < frame.width + margin && pose.y > frame.horizonY - 40 && pose.y < frame.height + margin;
}

export class ProjectionContext {
  frame: ProjectionFrame;
  motion: ViewMotion = { ...NO_MOTION };

  constructor(viewport: ViewportSize) {
    this.frame = createProjectionFrame(viewport);
  }

  setViewport(viewport: ViewportSize): void {
    this.frame = createProjectionFrame(viewport);
  }

  setMotion(motion: ViewMotion): void {
    this.motion = motion;
  }

  project(distance: number, lane: number): ProjectedPose {
    return projectLane(distance, lane, this.frame, this.motion);
  }

  projectObject(object: ProjectedWorldObject): ProjectedPose {
    return projectWorld(object, this.frame, this.motion);
  }
}

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}
